import React, { useState } from 'react'
import { router, Head } from '@inertiajs/react'
import OnboardingShell from '@/components/onboarding/OnboardingShell'
import CredentialForm from '@/components/onboarding/CredentialForm'
import PropertySelector from '@/components/onboarding/PropertySelector'
import type { CurrentAccount } from '@/types/shared'

interface Credential {
  id: number
  clientId: string
  environment: string
}

interface PropertyResult {
  shortname: string
  name: string
  currency: string
  status: string
  already_onboarded: boolean
  error?: string
}

interface IndexProps {
  currentUser: { id: number; email: string }
  credentials: Credential[]
  /**
   * The business being onboarded into — the FULL account summary
   * (Operator::OnboardingController#onboarding_props → current_account_summary), so its
   * `basePath` is the shell's way back to the briefing. Onboarding carries no :account_slug in
   * the path, which is exactly why the business has to arrive as a prop.
   */
  targetAccount: CurrentAccount | null
  // False on the real production deployment — see EviivoCredential.staging_selectable?
  allowStagingEnvironment: boolean
}

/**
 * Steps 1–2 of the onboarding journey (Connect → Choose property), rendered in
 * the shared OnboardingShell so the whole journey lives in ONE visual world —
 * the operator design system — from first click to final handoff.
 */
export default function Index({ currentUser, credentials, targetAccount, allowStagingEnvironment }: IndexProps) {
  const [step, setStep] = useState(1)
  const [credentialId, setCredentialId] = useState<number | null>(null)
  const [properties, setProperties] = useState<PropertyResult[]>([])

  function handleCredentialSuccess(data: { credentialId: number; properties: PropertyResult[] }) {
    setCredentialId(data.credentialId)
    setProperties(data.properties)
    setStep(2)
  }

  function handlePropertySelect(propertyId: number, redirectTo: string) {
    // Continue to the Review step (which has property context) via Inertia.
    router.visit(redirectTo)
  }

  function handleStepSelect(target: number) {
    // The stepper only offers COMPLETED stations; here that can only be step 1.
    if (target === 1) setStep(1)
  }

  return (
    <OnboardingShell
      currentStep={step}
      onStepSelect={step === 2 ? handleStepSelect : undefined}
      contextChip={targetAccount ? { label: 'Adding to', value: targetAccount.name, kind: 'account' } : undefined}
      signedInAs={currentUser.email}
      portfolioPath={targetAccount?.basePath ?? null}
    >
      <Head title="Add property" />
      {step === 1 && (
        <CredentialForm
          credentials={credentials}
          allowStagingEnvironment={allowStagingEnvironment}
          onSuccess={handleCredentialSuccess}
        />
      )}

      {step === 2 && credentialId !== null && (
        <PropertySelector
          credentialId={credentialId}
          properties={properties}
          accountName={targetAccount?.name ?? null}
          onBack={() => setStep(1)}
          onSelect={handlePropertySelect}
        />
      )}
    </OnboardingShell>
  )
}
